import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumbs from '../components/Breadcrumbs';
import KitSubscriptionForm from '../components/KitSubscriptionForm';
import { getAllBlogPosts } from '../data/blogPosts';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiArrowRight, FiCalendar, FiSearch, FiChevronLeft, FiChevronRight, FiTag, FiGrid } = FiIcons;

const POSTS_PER_PAGE = 15;

/**
 * Blog Listing Page
 * Displays all blog posts with ItemList + CollectionPage schema,
 * editorial intro, featured post, pagination, sidebar, and search/filter.
 */
export default function Blog() {
  const posts = getAllBlogPosts();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(posts.map((post) => post.category)))],
    [posts]
  );

  // Collect all tags with counts for sidebar
  const tagCounts = useMemo(() => {
    const counts = {};
    posts.forEach((post) => {
      if (Array.isArray(post.tags)) {
        post.tags.forEach((tag) => {
          counts[tag] = (counts[tag] || 0) + 1;
        });
      }
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 15);
  }, [posts]);

  // Category counts for sidebar
  const categoryCounts = useMemo(() => {
    const counts = {};
    posts.forEach((post) => {
      counts[post.category] = (counts[post.category] || 0) + 1;
    });
    return counts;
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
      const keyword = searchTerm.trim().toLowerCase();

      if (!keyword) return matchesCategory;

      const inTitle = post.title.toLowerCase().includes(keyword);
      const inExcerpt = post.excerpt.toLowerCase().includes(keyword);
      const inTags =
        Array.isArray(post.tags) &&
        post.tags.some((tag) => tag.toLowerCase().includes(keyword));

      return matchesCategory && (inTitle || inExcerpt || inTags);
    });
  }, [posts, searchTerm, selectedCategory]);

  // Featured post is the most recent post (only shown on page 1 with no filters)
  const isUnfiltered = selectedCategory === 'All' && !searchTerm.trim();
  const featuredPost = isUnfiltered ? filteredPosts[0] : null;
  const gridPosts = featuredPost ? filteredPosts.slice(1) : filteredPosts;

  // Pagination
  const totalPages = Math.max(1, Math.ceil(gridPosts.length / POSTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedPosts = gridPosts.slice(
    (safePage - 1) * POSTS_PER_PAGE,
    safePage * POSTS_PER_PAGE
  );

  // Reset to page 1 when filters change
  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
  };
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  const handleTagClick = (tag) => {
    setSearchTerm(tag);
    setSelectedCategory('All');
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // JSON-LD structured data
  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Green Gas Turbines Blog — Expert Analysis on Hydrogen Turbines & Clean Power',
      description: 'In-depth technical articles on hydrogen-ready gas turbines, combustion dynamics, retrofit economics, RNG fuel pathways, predictive maintenance, and decarbonization strategies for industrial power generation.',
      url: 'https://www.greengasturbines.com/blog',
      publisher: {
        '@type': 'Organization',
        name: 'Green Gas Turbines',
        logo: {
          '@type': 'ImageObject',
          url: 'https://www.greengasturbines.com/favicon.png'
        }
      },
      mainEntity: {
        '@type': 'ItemList',
        numberOfItems: posts.length,
        itemListElement: posts.slice(0, 20).map((post, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: `https://www.greengasturbines.com/blog/${post.slug}`,
          name: post.title
        }))
      }
    }
  ];

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Blog', path: null }
  ];

  return (
    <>
      <Helmet>
        <title>Green Gas Turbines Blog — Expert Guides on Hydrogen Turbines, Retrofits & Clean Power (2026)</title>
        <meta
          name="description"
          content={`${posts.length}+ in-depth articles on hydrogen-ready gas turbines: combustion dynamics, H₂ retrofit costs, RNG fuel pathways, predictive maintenance, and decarbonization strategies. Written by engineers for engineers.`}
        />
        <link rel="canonical" href="https://www.greengasturbines.com/blog" />

        {/* Open Graph */}
        <meta property="og:title" content="Green Gas Turbines Blog — Hydrogen Turbine Guides & Analysis" />
        <meta property="og:description" content={`${posts.length}+ expert articles on H₂-ready gas turbines, retrofit economics, combustion tech & clean power strategies.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.greengasturbines.com/blog" />
        <meta property="og:image" content="https://www.greengasturbines.com/green-gas-turbines.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Green Gas Turbines blog covering hydrogen-ready gas turbine technology and clean energy analysis" />
        <meta property="og:site_name" content="Green Gas Turbines" />
        <meta property="og:locale" content="en_US" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@GreenGasTs" />
        <meta name="twitter:title" content="Green Gas Turbines Blog — Hydrogen Turbine Guides & Analysis" />
        <meta name="twitter:description" content={`${posts.length}+ expert articles on H₂-ready gas turbines, retrofit economics & clean power strategies.`} />
        <meta name="twitter:image" content="https://www.greengasturbines.com/green-gas-turbines.jpg" />
        <meta name="twitter:image:alt" content="Green Gas Turbines blog covering hydrogen-ready gas turbine technology and clean energy analysis" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-secondary-900 to-secondary-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-1.5 bg-emerald-500/20 text-emerald-300 text-xs font-medium px-3 py-1 rounded-full border border-emerald-400/30 mb-4">
                <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></span>
                {posts.length}+ Expert Articles
              </span>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white via-emerald-100 to-white bg-clip-text text-transparent drop-shadow-lg">
                Green Gas Turbines Blog
              </h1>
              <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-4 leading-relaxed">
                In-depth technical articles, retrofit case studies, and market analysis
                for engineers, operators, and investors navigating the hydrogen energy transition.
              </p>
              <p className="text-sm text-emerald-400/80 font-medium">
                Updated weekly · Written by Industry Engineers · {posts.length} Articles
              </p>
            </motion.div>
          </div>
        </section>

        {/* Editorial Intro — unique content for AdSense depth */}
        <section className="py-12 bg-white border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="prose prose-lg max-w-none text-gray-700"
            >
              <h2 className="text-2xl font-bold text-secondary-900 mb-4">
                Why We Write About Green Gas Turbines
              </h2>
              <p>
                The global gas turbine fleet generates over 1,700 GW of installed capacity—roughly 23% of the world's electricity.
                Decarbonizing these assets is not optional; it is the single largest lever available to cut power-sector CO₂ emissions
                without sacrificing grid reliability. Yet the engineering details of hydrogen combustion, fuel-flexible retrofits,
                and renewable gas pathways remain scattered across vendor whitepapers, academic journals, and conference proceedings.
              </p>
              <p>
                This blog exists to consolidate that knowledge into actionable, peer-reviewed-quality articles written by
                practising engineers. Every piece covers real-world data—flame speeds, NOx formation thresholds, LCOE breakdowns,
                material compatibility charts—so you can make informed decisions about your fleet's transition pathway. Whether you
                are evaluating a 30% hydrogen blend for an existing Frame 6B or sizing an electrolyser for a greenfield combined-cycle
                plant, you will find the technical depth you need here.
              </p>
              <p>
                Topics span the full lifecycle: <strong>combustion dynamics</strong> (DLN vs. diffusion, flashback mitigation),
                <strong>hydrogen safety</strong> (hazard zoning, leak detection, venting design), <strong>retrofit economics</strong>
                (CapEx/OpEx modelling, insurance implications, tax credits), <strong>alternative fuels</strong> (RNG, ammonia,
                synthetic methane, biogas cleanup), and <strong>digital operations</strong> (predictive maintenance, SCADA integration,
                anomaly detection). Use the search and category filters below to find exactly what you need.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Featured Post (page 1, no filters) */}
        {featuredPost && currentPage === 1 && (
          <section className="py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Breadcrumbs items={breadcrumbItems} />
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="mt-6"
              >
                <Link to={`/blog/${featuredPost.slug}`} className="block group">
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden grid grid-cols-1 lg:grid-cols-2 hover:shadow-3xl transition-all duration-300 border border-gray-100">
                    <div className="relative h-64 lg:h-auto overflow-hidden">
                      <img
                        src={featuredPost.featuredImage.url}
                        alt={featuredPost.featuredImage.alt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
                      />
                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide shadow-lg">
                          Featured
                        </span>
                        <span className="bg-white/90 backdrop-blur-sm text-secondary-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                          {featuredPost.category}
                        </span>
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity duration-300"></div>
                    </div>
                    <div className="p-8 lg:p-10 flex flex-col justify-center bg-gradient-to-br from-white to-gray-50">
                      <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
                        <div className="flex items-center">
                          <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-1 text-primary-500" />
                          <time dateTime={featuredPost.publishDate}>
                            {new Date(featuredPost.publishDate).toLocaleDateString('en-US', {
                              year: 'numeric', month: 'long', day: 'numeric'
                            })}
                          </time>
                        </div>
                        <div className="flex items-center">
                          <SafeIcon icon={FiClock} className="h-4 w-4 mr-1 text-primary-500" />
                          <span>{featuredPost.readTime}</span>
                        </div>
                      </div>
                      <h2 className="text-3xl font-bold text-secondary-900 mb-4 group-hover:text-primary-600 transition-colors leading-tight">
                        {featuredPost.title}
                      </h2>
                      <p className="text-gray-600 mb-6 line-clamp-3 text-lg leading-relaxed">
                        {featuredPost.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-6">
                        {featuredPost.tags.slice(0, 4).map((tag) => (
                          <span key={tag} className="text-xs bg-primary-50 text-primary-700 px-3 py-1.5 rounded-full font-medium hover:bg-primary-100 transition-colors cursor-pointer">
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <span className="inline-flex items-center text-primary-600 font-semibold group-hover:text-primary-700 transition-colors text-sm">
                        Read Full Article
                        <SafeIcon icon={FiArrowRight} className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            </div>
          </section>
        )}

        {/* Main Content: Posts Grid + Sidebar */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Show breadcrumbs here if no featured post */}
            {(!featuredPost || currentPage !== 1) && <Breadcrumbs items={breadcrumbItems} />}

            {/* Search & Filter Bar */}
            <div className="mt-6 mb-10 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-secondary-900 flex items-center gap-2">
                  <SafeIcon icon={FiGrid} className="h-5 w-5 text-primary-600" />
                  {isUnfiltered ? 'All Articles' : `${filteredPosts.length} Result${filteredPosts.length !== 1 ? 's' : ''}`}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Page {safePage} of {totalPages} &middot; {filteredPosts.length} article{filteredPosts.length !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                <div className="relative flex-1 md:w-64">
                  <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <label htmlFor="blog-search" className="sr-only">Search articles</label>
                  <input
                    id="blog-search"
                    type="text"
                    placeholder="Search articles..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full rounded-lg border border-gray-300 bg-white pl-10 pr-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div className="w-full sm:w-48">
                  <label htmlFor="blog-category" className="sr-only">Filter by category</label>
                  <select
                    id="blog-category"
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
              {/* Posts Grid */}
              <div className="lg:col-span-3">
                {paginatedPosts.length === 0 ? (
                  <div className="text-center py-16 text-gray-500">
                    <p className="text-lg font-medium mb-2">No articles match your search.</p>
                    <p className="text-sm">Try adjusting your keywords or category filter.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {paginatedPosts.map((post, index) => (
                      <motion.article
                        key={post.id}
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-50px" }}
                        transition={{ duration: 0.5, delay: index * 0.03 }}
                        className="group bg-white rounded-2xl shadow-md hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300 overflow-hidden flex flex-col border border-gray-100"
                      >
                        <Link to={`/blog/${post.slug}`}>
                          <div className="relative h-52 overflow-hidden">
                            <img
                              src={post.featuredImage.url}
                              alt={post.featuredImage.alt}
                              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                            <div className="absolute top-3 left-3">
                              <span className="bg-gradient-to-r from-primary-600 to-primary-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold shadow-lg">
                                {post.category}
                              </span>
                            </div>
                            <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <span className="text-xs font-medium bg-black/50 backdrop-blur-sm px-2 py-1 rounded">
                                Read Article
                              </span>
                              <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                            </div>
                          </div>
                        </Link>

                        <div className="p-5 flex flex-col flex-1">
                          <div className="flex items-center text-xs text-gray-500 mb-3 space-x-3">
                            <div className="flex items-center">
                              <SafeIcon icon={FiCalendar} className="h-3.5 w-3.5 mr-1 text-primary-500" />
                              <time dateTime={post.publishDate}>
                                {new Date(post.publishDate).toLocaleDateString('en-US', {
                                  year: 'numeric', month: 'short', day: 'numeric'
                                })}
                              </time>
                            </div>
                            <div className="flex items-center">
                              <SafeIcon icon={FiClock} className="h-3.5 w-3.5 mr-1 text-primary-500" />
                              <span>{post.readTime}</span>
                            </div>
                          </div>

                          <Link to={`/blog/${post.slug}`}>
                            <h3 className="text-lg font-bold text-secondary-900 mb-3 hover:text-primary-600 transition-colors line-clamp-2 group-hover:text-primary-600">
                              {post.title}
                            </h3>
                          </Link>

                          <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1 leading-relaxed">
                            {post.excerpt}
                          </p>

                          <div className="flex flex-wrap gap-1.5 mb-4">
                            {post.tags.slice(0, 3).map((tag) => (
                              <span key={tag} className="text-xs bg-gray-100 text-gray-600 hover:bg-primary-50 hover:text-primary-600 px-2 py-1 rounded-full transition-colors cursor-pointer">
                                #{tag}
                              </span>
                            ))}
                          </div>

                          <Link
                            to={`/blog/${post.slug}`}
                            className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm mt-auto group/link"
                          >
                            Read More
                            <SafeIcon icon={FiArrowRight} className="ml-1.5 h-4 w-4 group-hover/link:translate-x-1 transition-transform" />
                          </Link>
                        </div>
                      </motion.article>
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <nav aria-label="Blog pagination" className="mt-12 flex items-center justify-center gap-2">
                    <button
                      onClick={() => { setCurrentPage((p) => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      disabled={safePage === 1}
                      className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <SafeIcon icon={FiChevronLeft} className="h-4 w-4" />
                      Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                          page === safePage
                            ? 'bg-primary-600 text-white shadow-md'
                            : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => { setCurrentPage((p) => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                      disabled={safePage === totalPages}
                      className="flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                      <SafeIcon icon={FiChevronRight} className="h-4 w-4" />
                    </button>
                  </nav>
                )}
              </div>

              {/* Sidebar */}
              <aside className="lg:col-span-1 space-y-6">
                {/* Categories */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                  <h3 className="text-lg font-bold text-secondary-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                      <SafeIcon icon={FiGrid} className="h-4 w-4 text-white" />
                    </span>
                    Categories
                  </h3>
                  <ul className="space-y-1.5">
                    {categories.filter((c) => c !== 'All').map((cat) => (
                      <li key={cat}>
                        <button
                          onClick={() => handleCategoryChange(cat)}
                          className={`w-full text-left flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all ${
                            selectedCategory === cat
                              ? 'bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 font-semibold shadow-sm'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <span>{cat}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            selectedCategory === cat ? 'bg-primary-200 text-primary-800' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {categoryCounts[cat] || 0}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Popular Tags */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                  <h3 className="text-lg font-bold text-secondary-900 mb-4 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center">
                      <SafeIcon icon={FiTag} className="h-4 w-4 text-white" />
                    </span>
                    Popular Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tagCounts.map(([tag, count]) => (
                      <button
                        key={tag}
                        onClick={() => handleTagClick(tag)}
                        className="text-sm bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full hover:bg-gradient-to-r hover:from-primary-500 hover:to-primary-600 hover:text-white transition-all duration-300 shadow-sm hover:shadow-md"
                        title={`${count} article${count !== 1 ? 's' : ''}`}
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Newsletter CTA */}
                <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl shadow-lg p-6 text-white">
                  <KitSubscriptionForm
                    variant="minimal"
                    title="Weekly Turbine Digest"
                    description="Get the latest hydrogen turbine analysis, retrofit guides, and market data delivered to your inbox."
                  />
                </div>

                {/* Quick Links */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-secondary-900 mb-3 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                      <FiIcons.FiZap className="h-4 w-4 text-white" />
                    </span>
                    Engineering Tools
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                    Explore our free interactive calculators for hydrogen blending, emissions modelling, and retrofit ROI analysis.
                  </p>
                  <Link
                    to="/interactive-tools"
                    className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors text-sm bg-white px-4 py-2 rounded-full shadow-sm hover:shadow-md"
                  >
                    Browse All Tools
                    <SafeIcon icon={FiArrowRight} className="ml-1.5 h-4 w-4" />
                  </Link>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
